import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Zap, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function QuickStartGuide({ stats }) {
  const { t } = useTranslation();
  const [walletData, setWalletData] = useState(null);
  const [allocData, setAllocData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const api = axios.create({ baseURL: BACKEND_URL, withCredentials: true, adapter: 'xhr' });
    Promise.all([
      api.get('/api/wallet/balance'),
      api.get('/api/allocations/me'),
    ]).then(([w, a]) => {
      setWalletData(w.data);
      setAllocData(a.data);
    }).catch(() => {});
  }, []);

  if (dismissed) return null;
  if (!walletData) return null;

  const hasDeposit = walletData.total_deposited > 0;
  const hasAllocation = allocData?.allocations?.length > 0;
  const allDone = hasDeposit && hasAllocation;

  if (allDone) return null;

  const steps = [
    { num: 1, label: 'Deposit Funds', desc: 'Add capital to your wallet to get started', done: hasDeposit, link: '/wallet', cta: 'Go to Wallet' },
    { num: 2, label: 'Activate Agents', desc: 'Assign capital from your wallet to the AI agents', done: hasAllocation, link: '/wallet', cta: 'Assign Capital' },
  ];

  return (
    <div className="bg-gradient-to-r from-emerald-500/10 via-[#090B0F] to-[#090B0F] border border-emerald-500/20 p-5" data-testid="quick-start-guide">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-emerald-500/20 flex items-center justify-center">
            <Zap className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">{t('dashboard.quickStart')}</h3>
            <p className="text-xs text-slate-400">{t('dashboard.quickStartDesc')}</p>
          </div>
        </div>
        <button onClick={() => setDismissed(true)} className="text-slate-600 hover:text-white text-xs font-mono">{t('dashboard.closeGuide')}</button>
      </div>
      <div className="flex gap-4">
        {steps.map(step => (
          <Link key={step.num} to={step.link}
            className={`flex-1 p-4 border transition-all group ${step.done ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-white/10 hover:border-emerald-500/30 bg-[#030508]'}`}
            data-testid={`quick-start-step-${step.num}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className={`w-6 h-6 flex items-center justify-center text-xs font-bold ${step.done ? 'bg-emerald-500 text-[#030508]' : 'bg-white/10 text-slate-400'}`}>
                {step.done ? '\u2713' : step.num}
              </div>
              <span className={`text-sm font-bold ${step.done ? 'text-emerald-400' : 'text-white'}`}>{step.label}</span>
            </div>
            <p className="text-xs text-slate-500 mb-3">{step.desc}</p>
            {!step.done && (
              <span className="text-xs font-mono text-emerald-400 group-hover:text-emerald-300 flex items-center gap-1">
                {step.cta} <ChevronRight className="w-3 h-3" />
              </span>
            )}
            {step.done && <span className="text-xs font-mono text-emerald-500/60">{t('dashboard.completed')}</span>}
          </Link>
        ))}
      </div>
    </div>
  );
}
